import React, {useState, useCallback} from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import DataGrid, {DataGridItem} from "components/data-grid";
import {injectIntl, FormattedMessage} from "react-intl";
import {Restricted, useAuthDataContext, permissions} from "components/auth";
import styles from "./styles/actions.css.js";
import withStyles from "components/with-styles";
import {InsertDialog} from "./links";
import {SimpleButton} from "components/buttons";
import {useImperativeTracking} from "components/tracking";
import {isMobileOnly} from "react-device-detect";
import Dialog from "components/dialog";
import {eventStatusTypes} from "pages/event/utils";
import {PageSection} from "components/page";

const EventData = ({event, intl, classes}) => {
    const [open, setOpen] = useState(false);
    const {user} = useAuthDataContext();
    const track = useImperativeTracking();
    const remoteProductionUrl = event.urlRemoteProduction ? `${event.urlRemoteProduction}&locale=${user.locale}` : null;

    const handleClick = e => {
        e.stopPropagation();
        track("access to remote production url " + remoteProductionUrl + " for event " + event.id);
    };

    const handleOpen = useCallback(
        e => {
            setOpen(true);
            track("opens second coach dialog");
            e.stopPropagation();
        },
        [track],
    );

    const handleClose = useCallback(
        e => {
            setOpen(false);
            track("closes second coach dialog");
            e.stopPropagation();
        },
        [track],
    );

    const showActions = event?.eventStatus === eventStatusTypes.STARTED;
    const showRemoteProduction = !!event.remoteProductionAvailable;

    return (
        <>
            {showActions ? <PageSection spaced>
                <Restricted permissions={[permissions.mixer_manage]}>
                    {showRemoteProduction ?
                        <DataGrid>
                            <DataGridItem sizesOverride={{xl: 6}}>
                                <SimpleButton
                                    target={`remoteprod_${event.id}`}
                                    href={remoteProductionUrl || null}
                                    icon="camera"
                                    label={intl.formatMessage({id: "event.remoteProduction"})}
                                    rel="noopener noreferrer"
                                    className={cn({
                                        [classes.toolButton]: !isMobileOnly,
                                        [classes.toolButton_text__small]: isMobileOnly
                                    })}
                                    onClick={remoteProductionUrl ? handleClick : handleOpen}
                                />
                                <Dialog open={open} onClose={handleClose}>
                                    <FormattedMessage id="event.remoteProduction.dialog"/>
                                </Dialog>
                            </DataGridItem>
                        </DataGrid> :
                        <DataGrid>
                            <DataGridItem big>
                                    <span className={classes.remote_production_available_error}>
                                        {intl.formatMessage({id: "event.mixer.not.available"})}{" "}
                                    </span>
                            </DataGridItem>
                        </DataGrid>}
                </Restricted>
                <DataGrid>
                    <Restricted permissions={[permissions.mixer_manage]}>
                        <DataGridItem sizesOverride={{xl: 6}}>
                            <SimpleButton
                                href={`/event/live-emision/${event.id}/customer/${event.customerId}`}
                                icon="play-circle-outline"
                                label={intl.formatMessage({id: "event.liveEmision"})}
                                rel="noopener noreferrer"
                                variant="outlined"
                                className={cn({
                                    [classes.toolButton]: !isMobileOnly,
                                    [classes.toolButton_text__small]: isMobileOnly,
                                })}
                                onClick={e => {
                                    e.stopPropagation();
                                    track("access to live event emision for event " + event.id);
                                }}
                            />
                        </DataGridItem>
                    </Restricted>
                    <Restricted permissions={[permissions.event_components_manage]}>
                        <DataGridItem sizesOverride={{xl: 6}}>
                            <SimpleButton
                                href={`/event/algorithm-viewer/${event.id}/customer/${event.customerId}`}
                                target={`debug_camop${event.id}`}
                                icon="center-focus-strong"
                                label={intl.formatMessage({id: "event.iaALgorithmViewer"})}
                                rel="noopener noreferrer"
                                variant="outlined"
                                className={cn({
                                    [classes.toolButton]: !isMobileOnly,
                                    [classes.toolButton_text__small]: isMobileOnly,
                                })}
                                onClick={e => {
                                    e.stopPropagation();
                                    track("access to ia algorithm for event " + event.id);
                                }}
                            />
                        </DataGridItem>
                    </Restricted>
                    <Restricted permissions={[permissions.mixer_manage]}>
                        <DataGridItem sizesOverride={{xl: 6}}>
                            <InsertDialog
                                isCamera
                                className={classes.dialog}
                                buttonClassName={cn({
                                    [classes.toolButton]: !isMobileOnly,
                                    [classes.dialog_text__small]: isMobileOnly,
                                })}
                                event={event}
                                onClick={e => {
                                    e.stopPropagation();
                                    track("access to external camera dialog for event " + event.id);
                                }}
                            />
                        </DataGridItem>
                    </Restricted>
                    <Restricted permissions={[permissions.mixer_manage]}>
                        <DataGridItem sizesOverride={{xl: 6}}>
                            <InsertDialog
                                className={classes.dialog}
                                buttonClassName={cn({
                                    [classes.toolButton]: !isMobileOnly,
                                    [classes.dialog_text__small]: isMobileOnly,
                                })}
                                event={event}
                                onClick={e => {
                                    e.stopPropagation();
                                    track("access to audio dialog for event " + event.id);
                                }}
                            />
                        </DataGridItem>
                    </Restricted>
                </DataGrid>
            </PageSection> : null}
        </>
    )
};

EventData.propTypes = {
    /**
     * Data to represent
     **/
    event: PropTypes.object,
};

export default withStyles(styles)(injectIntl(EventData));
